import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Modal, Space, Table, Tooltip, message } from 'antd';
import axios from 'axios';
import { PRIMARY_RED } from 'constants/colors';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { setBreadcrumbItems } from "../../store/actions";
import { connect } from 'react-redux';
import moment from 'moment';

const { confirm } = Modal;

const OrderList = (props) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const authUser = JSON.parse(localStorage.getItem('authUser'));
  const token = authUser?.token;

  const fetchOrders = async (page = 1, pageSize = 10) => {
    if (!token) {
      message.error('Authentication token not found');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASEURL}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawData = response.data || [];
      const formattedOrders = rawData.map((order) => ({
        ...order,
        key: order._id,
        orderShortCode: order.orderShortCode || 'N/A',
        orderStatus: order.orderStatus || 'N/A',
        requestFrom:
          order.requestFrom?.employeeName ||
          order.requestedFromRegionalManager?.employeeName ||
          'N/A',
        requestTo:
          order.requestTo?.employeeName ||
          order.requestedToSuperAdmin?.userName ||
          'N/A',
        date: moment(order.createdAt).format('DD MMM YYYY, hh:mm A'),
        remarks: order.remarks || 'N/A',
        orderImages: order.orderImages || [],
      }));

      setOrders(formattedOrders);
      setPagination({ current: page, pageSize, total: rawData.length });
    } catch (error) {
      message.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { title: "Orders", link: "#" },
    { title: "Order Listing", link: "#" },
  ];

  useEffect(() => {
    props.setBreadcrumbItems("Orders", breadcrumbItems);
    fetchOrders(pagination.current, pagination.pageSize);
  }, []);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [searchText]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASEURL}/orders/update-on-fly/${id}`,
        { orderStatus: status, action: 'onFly' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`Order ${status === 'dispatched' ? 'dispatched' : 'rejected'} successfully`);
      fetchOrders(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const exportToCSV = () => {
    const csvRows = [];
    const headers = ["Order Code", "Date", "Remarks", "Products", "Order Status", "Request From", "Request To"];
    csvRows.push(headers.join(","));

    orders.forEach(order => {
      const escape = (text) => `"${(text || '').toString().replace(/"/g, '""')}"`;

      const productsStr = order.product?.map(p =>
        `${p.productName || 'Unknown'} (x${p.quantity})`
      ).join("; ");

      const row = [
        escape(order.orderShortCode),
        escape(order.date),
        escape(order.remarks),
        escape(productsStr),
        escape(order.orderStatus),
        escape(order.requestFrom),
        escape(order.requestTo)
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "orders_list.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (orderId, orderShortCode) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASEURL}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(`Deleted order: ${orderShortCode}`);
      fetchOrders(pagination.current, pagination.pageSize); // refresh list
    } catch (err) {
      message.error("Failed to delete order");
    }
  };

  const showDeclineConfirm = (order) => {
    confirm({
      title: `Decline order ${order.orderShortCode}?`,
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to decline this order?',
      okText: 'Decline',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        changeStatus(order._id, 'rejected');
      },
    });
  };

  const columns = [
    {
      title: 'Order Code',
      dataIndex: 'orderShortCode',
      key: 'orderShortCode',
      render: (text) => <b>{text}</b>,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
    },
    {
      title: 'Products',
      dataIndex: 'product',
      key: 'product',
      render: (products, record) => (
        <div style={{ maxHeight: 60, overflowY: 'auto', minWidth: 150 }}>
          {record.product?.map((p, i) => (
            <div key={i} style={{ whiteSpace: 'nowrap' }}>
              {p.productName || 'Unknown'} <span style={{ fontWeight: 'bold' }}>(x{p.quantity})</span>
            </div>
          ))}
        </div>
      )
    },
    {
      title: 'Images',
      dataIndex: 'orderImages',
      key: 'orderImages',
      render: (images, record) => {
        const productImages = record.product?.map(p =>
          p.product_id?.productImgUrl?.[0] ? `data:image/jpeg;base64,${p.product_id.productImgUrl[0]}` : null
        ).filter(Boolean) || [];

        const displayImages = [...(images || []), ...productImages];

        return (
          <Space>
            {displayImages.length > 0 ? (
              displayImages.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt="Order"
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                  onClick={() => {
                    const win = window.open();
                    win.document.write('<iframe src="' + img + '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>');
                  }}
                />
              ))
            ) : (
              <span>No Images</span>
            )}
          </Space>
        )
      },
    },
    {
      title: 'Order Status',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      render: (_, record) => {
        const status = record.orderStatus.toLowerCase();
        if (status === 'approved') {
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                style={{ backgroundColor: PRIMARY_RED }}
                onClick={() => changeStatus(record._id, 'dispatched')}
              >
                Approve
              </Button>
              <Button type="link" style={{ color: PRIMARY_RED }} onClick={() => showDeclineConfirm(record)}>
                Decline
              </Button>
            </Space>
          );
        } else {
          return (
            <span style={{ textTransform: 'capitalize' }}>
              {status === 'dispatched' ? 'Dispatched' : status === 'rejected' ? 'Rejected' : status}
            </span>
          );
        }
      },
    },
    {
      title: 'Request From',
      dataIndex: 'requestFrom',
      key: 'requestFrom',
    },
    {
      title: 'Request To',
      dataIndex: 'requestTo',
      key: 'requestTo',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <EditOutlined
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/edit-order/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined
              style={{ color: PRIMARY_RED, cursor: 'pointer' }}
              onClick={() => {
                setSelectedOrder(record);
                setIsDeleteModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filteredOrders = orders.filter(order => {
    const query = searchText.toLowerCase();
    return (
      order.orderShortCode.toLowerCase().includes(query) ||
      order.orderStatus.toLowerCase().includes(query) ||
      order.requestFrom.toLowerCase().includes(query) ||
      order.requestTo.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <input
          type="text"
          placeholder="Search orders..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 250, padding: '6px 10px' }}
        />
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportToCSV}
            style={{ marginRight: 10 }}
          >
            Export Excel
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ backgroundColor: PRIMARY_RED }}
            onClick={() => navigate("/add-order")}
          >
            Add Order
          </Button>
        </Space>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Order"
        visible={isDeleteModalVisible}
        onCancel={() => setIsDeleteModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsDeleteModalVisible(false)}
            style={{ backgroundColor: "#6c757d", color: "white" }}
          >
            Close
          </Button>,
          <Button
            key="delete"
            type="primary"
            style={{ backgroundColor: PRIMARY_RED }}
            icon={<DeleteOutlined />}
            onClick={() => {
              handleDelete(selectedOrder._id, selectedOrder.orderShortCode);
              setIsDeleteModalVisible(false);
            }}
          >
            Delete
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to delete order{" "}
          <strong>{selectedOrder?.orderShortCode}</strong>?
        </p>
      </Modal>

      <Table
        dataSource={filteredOrders}
        columns={columns}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: filteredOrders.length,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) =>
            `Rows per page: ${range[0]}–${range[1]} of ${total}`,
        }}
        onChange={handleTableChange}
        bordered={false}
      />
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(OrderList);
